import React from 'react';
import { getRedirectTo } from '../../auth/redirect';
import { isPreviewRuntime, resolveRuntimeOrigin } from '../../lib/runtimeEnv';
import useSessionStore from '../../state/session';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithAzureOAuth } from '../../data/auth';
import {
  consumeStoredReturnPath,
  readStoredReturnPath,
  validateReturnPath,
} from '../../auth/routePolicy';
import { safeErrorMessage } from '../../utils/appErrors';
import { apiFetch } from '../../api/http';
import PortalAcronym from '../../components/common/PortalAcronym';
import { createGuestAuthClient } from '../../data/auth';
import { getSupabaseClient } from '../../data/supabase';
import useGuestSecurity from '../../hooks/useGuestSecurity';
import TurnstileChallenge from '../../components/auth/TurnstileChallenge';
import { requestGuestRecovery } from '../../api/guestAuth';
import './LoginPage.css';

export default function LoginPage() {
  const [signingIn, setSigningIn] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const security = useGuestSecurity();
  const guestClient = React.useMemo(createGuestAuthClient, []);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [guestBusy, setGuestBusy] = React.useState(false);
  const [invalidField, setInvalidField] = React.useState<'email' | 'password' | 'code' | null>(null);
  const [guestMessage, setGuestMessage] = React.useState('');
  const [captchaToken, setCaptchaToken] = React.useState('');
  const [captchaReset, setCaptchaReset] = React.useState(0);
  const [recovery, setRecovery] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [factorId, setFactorId] = React.useState('');
  const [code, setCode] = React.useState('');
  const guestInFlight = React.useRef(false);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSessionStore(s=>s.session);
  const authStatus = useSessionStore(s=>s.authStatus);
  const contextStatus = useSessionStore(s=>s.contextStatus);
  const loggedSuccessRef = React.useRef(false);
  const autoStartedRef = React.useRef(false);
  const signInInFlightRef = React.useRef(false);
  const getLastEmail = React.useCallback((): string | null => {
    try { return window.localStorage.getItem('last_login_email'); } catch { return null; }
  }, []);

  // After successful auth, send user to homepage instead of events listing
  React.useEffect(() => {
    if(session && authStatus === 'authenticated' && contextStatus === 'ready'){
      if (guestBusy || guestInFlight.current || factorId) return;
      if (!loggedSuccessRef.current) {
        loggedSuccessRef.current = true;
        // Best-effort success audit
        try {
          // Persist last known email for future login attempts (helps attribute failures)
          const em = (session as any)?.user?.email || null;
          if (em) { try { window.localStorage.setItem('last_login_email', em); } catch {}
          }
          void apiFetch(
            '/public/log-login',
            {
              method:'POST',
              credentials: 'same-origin',
              headers:{ 'Content-Type':'application/json' },
              body: JSON.stringify({ provider: session.user.app_metadata?.provider === 'email' ? 'email' : 'azure', succeeded:true, email: em || undefined })
            },
            { authMode: 'public' },
          );
        } catch {}
      }
      const stateFrom = validateReturnPath((location.state as { from?: string } | null)?.from);
      const from = stateFrom || consumeStoredReturnPath() || '/';
      navigate(from, { replace: true });
    }
  }, [authStatus, contextStatus, session, navigate, location.state, factorId, guestBusy]);

  const redirectTo = React.useMemo(() => {
    const stateFrom = validateReturnPath((location.state as { from?: string } | null)?.from);
    return getRedirectTo(stateFrom || readStoredReturnPath());
  }, [location.state]);
  const origin = React.useMemo(() => resolveRuntimeOrigin(), []);

  const startSignIn = React.useCallback(async (forcePrompt?: boolean) => {
      // Best-effort: log attempt start
      try {
        const lastEmail = getLastEmail();
        await apiFetch(
          '/public/log-login',
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ provider: 'azure', succeeded: false, error_code: 'attempt', error_description: 'User initiated OAuth login', email: lastEmail || undefined })
          },
          { authMode: 'public' },
        );
      } catch {}

      const lastEmail = getLastEmail();
      const { error } = await signInWithAzureOAuth({
        redirectTo,
        forcePrompt,
        loginHint: lastEmail,
      });
      if (error) throw error;
      return true;
  }, [getLastEmail, redirectTo]);
  const signInWithMicrosoft = React.useCallback(
    async (forcePrompt?: boolean) => {
      if (signInInFlightRef.current) return;
      signInInFlightRef.current = true;
      setSigningIn(true);
      setMessage(null);
      try {
        const previewDiag = isPreviewRuntime();
        if (previewDiag) {
          // eslint-disable-next-line no-console
          console.info('[diag] login: origin', origin ?? '(empty)');
          // eslint-disable-next-line no-console
          console.info('[diag] login: redirectTo', redirectTo);
        }
        await startSignIn(forcePrompt);
      } catch (err: unknown) {
        setMessage(safeErrorMessage(err, 'Sign-in could not be started.'));
      } finally {
        signInInFlightRef.current = false;
        setSigningIn(false);
      }
    },
    [origin, redirectTo, startSignIn],
  );

  // Auto-start SSO flow when redirected from logout with ?sso=1
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sso = params.get('sso') === '1';
    const expired = params.get('reason') === 'session_expired';
    const hasError = params.has('error') || params.has('error_code') || params.has('error_description');
    if (!session && sso && !expired && !hasError && !autoStartedRef.current) {
      // Claim the automatic start before mutation state can cause another render.
      autoStartedRef.current = true;
      void signInWithMicrosoft(true);
    }
  }, [session, location.search, signInWithMicrosoft]);

  // Show OAuth errors (e.g., signup_disabled) and log them server-side
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const error_code = params.get('error_code');
    const error_description = params.get('error_description');
    const reason = params.get('reason');
    if (reason === 'session_expired') {
      setMessage('Your session expired. Sign in again to continue.');
      return;
    }
    if (error || error_code || error_description) {
      const code = (error_code || error || '').toLowerCase();
      let msg = 'Sign-in could not be completed. Please try again.';
      if (code === 'signup_disabled' || code === 'signups_disabled') {
        msg = 'Your account is not provisioned. Ask an admin to add you.';
      } else if (code === 'session_not_ready') {
        msg = 'Sign-in did not finish. Please try again.';
      }
      setMessage(msg);
      // Best-effort: log failure with context
      (async () => {
        try {
          const lastEmail = getLastEmail();
          await apiFetch(
            '/public/log-login',
            {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ provider: 'azure', succeeded: false, error_code: error_code || error || 'oauth_error', email: lastEmail || undefined })
            },
            { authMode: 'public' },
          );
        } catch {}
      })();
      // Clean up query params to prevent repeated logs on re-render
      try {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('error');
        clean.searchParams.delete('error_code');
        clean.searchParams.delete('error_description');
        window.history.replaceState({}, '', clean.toString());
      } catch {}
    }
  }, [location.search]);

  async function promoteGuestSession() {
    const { data, error } = await guestClient.auth.getSession();
    if (error || !data.session) throw new Error('Sign-in did not finish. Please try again.');
    const { error: sessionError } = await getSupabaseClient().auth.setSession({ access_token: data.session.access_token, refresh_token: data.session.refresh_token });
    if (sessionError) throw new Error('Sign-in did not finish. Please try again.');
    setFactorId('');
  }
  async function submitGuest(event: React.FormEvent) {
    event.preventDefault();
    if (guestInFlight.current) return;
    setGuestMessage(''); setInvalidField(null);
    if (!factorId && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) { setInvalidField('email'); setGuestMessage('Enter your invitation email address.'); emailRef.current?.focus(); return; }
    if (!factorId && !recovery && !password) { setInvalidField('password'); setGuestMessage('Enter your password to continue.'); passwordRef.current?.focus(); return; }
    if (recovery && !confirmed) { setGuestMessage('Confirm the email address before requesting your reset link.'); return; }
    if (!factorId && (!security.data || security.isError)) { setGuestMessage('Guest sign-in settings are unavailable. Use Retry below.'); return; }
    if (!factorId && security.data?.enabled && !captchaToken) { setGuestMessage('Complete the security check and try again.'); return; }
    guestInFlight.current = true; setGuestBusy(true);
    try {
      if (factorId) {
        if (!/^\d{6}$/.test(code)) { setInvalidField('code'); document.getElementById('guest-code')?.focus(); throw new Error('Enter the six-digit code from your authenticator app.'); }
        const { error } = await guestClient.auth.mfa.challengeAndVerify({ factorId, code });
        if (error) throw new Error('That code could not be verified. Use the current code and try again.');
        await promoteGuestSession();
      } else if (recovery) {
        const result = await requestGuestRecovery(email.trim(), captchaToken);
        setGuestMessage(result.message);
      } else {
        const { error } = await guestClient.auth.signInWithPassword({ email: email.trim(), password, options: { captchaToken: captchaToken || undefined } });
        if (error) {
          if (error.code === 'captcha_failed') throw new Error('Security check failed. Complete a new check and try again.');
          if (error.status === 429) throw new Error('Too many sign-in attempts. Wait a few minutes and try again.');
          throw new Error('Sign-in could not be completed. Check your email and password, or contact your ITA coordinator.');
        }
        const { data: factors, error: factorError } = await guestClient.auth.mfa.listFactors();
        if (factorError) throw new Error('Two-step verification could not be checked. Please try again.');
        if (factors.totp.length) { setFactorId(factors.totp[0].id); setPassword(''); }
        else await promoteGuestSession();
      }
    } catch (error) { setGuestMessage(safeErrorMessage(error, 'Guest sign-in could not be completed. Please try again.')); }
    finally { guestInFlight.current = false; setGuestBusy(false); setCaptchaToken(''); setCaptchaReset(v => v + 1); }
  }

  return (
    <main className="portal-login">
      <header className="portal-login__intro">
        <span className="portal-login__eyebrow">Welcome to the Portal</span>
        <h1>Your work starts here.</h1>
        <PortalAcronym className="portal-login__meaning" />
        <p className="portal-login__choose">Choose the sign-in option that matches your account.</p>
      </header>
      <div className="portal-login__shell">
        <section className="portal-login__employee" aria-labelledby="employee-heading">
          <span className="portal-login__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 21V5h10v16M14 10h6v11M2 21h20M8 9h2M8 13h2M8 17h2M17 14h1M17 18h1" /></svg></span>
          <h2 id="employee-heading">ITA employees</h2>
          <p>Sign in with your ITA Microsoft account to open your workspace.</p>
          <button className="btn portal-login__microsoft" disabled={signingIn || guestBusy || Boolean(factorId)} aria-busy={signingIn} onClick={() => signInWithMicrosoft(true)}>
            <span className="portal-login__ms-logo" aria-hidden="true"><i /><i /><i /><i /></span>
            {signingIn ? 'Connecting…' : 'Continue with Microsoft'}
          </button>
          <p className="portal-login__hint">Use the account you use for ITA email.</p>
          {message && <p className="portal-login__message" role="status">{message}</p>}
          <div className="portal-login__employee-note"><strong>One account. Your ITA workspace.</strong><p>Your access is based on your assigned role and locations.</p></div>
        </section>
        <section className="portal-login__guest" aria-labelledby="guest-heading">
          <span className="portal-login__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></svg></span>
          <h2 id="guest-heading">Guests &amp; freelancers</h2>
          <p>{factorId ? 'Enter the code from your authenticator app.' : recovery ? 'Request a reset link for your invited account.' : 'Not an ITA employee? Sign in with the email address used for your invitation.'}</p>
          <form noValidate onSubmit={submitGuest} className="portal-login__form">
            <fieldset disabled={guestBusy || signingIn || authStatus === 'checking'}>
              {factorId ? <div className="portal-login__field"><label htmlFor="guest-code">Authenticator code</label><input id="guest-code" aria-invalid={invalidField === 'code'} className="input" autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} aria-describedby="guest-status" autoFocus /></div> : <>
                <div className="portal-login__field"><label htmlFor="guest-email">Email address</label><input id="guest-email" aria-invalid={invalidField === 'email'} className="input" ref={emailRef} type="email" autoComplete="username" value={email} onChange={e => { setEmail(e.target.value); setConfirmed(false); }} placeholder="you@example.com" aria-describedby="guest-status" /></div>
                {!recovery ? <div className="portal-login__field"><div className="portal-login__label-row"><label htmlFor="guest-password">Password</label><button type="button" className="portal-login__link" onClick={() => { setRecovery(true); setGuestMessage(''); }}>Forgot password?</button></div><div className="portal-login__password"><input id="guest-password" aria-invalid={invalidField === 'password'} className="input" ref={passwordRef} type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} aria-describedby="guest-status" /><button type="button" className="portal-login__show" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(v => !v)}>{showPassword ? 'Hide' : 'Show'}</button></div></div> : <label className="portal-login__confirmation"><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} /><span>Send a password reset link to {email.trim() || 'the email address above'}.</span></label>}
                {security.data?.enabled && <TurnstileChallenge siteKey={security.data.site_key} onToken={setCaptchaToken} resetKey={captchaReset} />}
              </>}
              <button className="btn btn-primary portal-login__submit" type="submit" aria-busy={guestBusy}>{guestBusy ? 'Please wait…' : factorId ? 'Verify and sign in' : recovery ? 'Send reset link' : 'Sign in'}</button>
              {(recovery || factorId) && <button className="portal-login__link" type="button" onClick={() => { setRecovery(false); setFactorId(''); setCode(''); setGuestMessage(''); }}>Back to guest sign-in</button>}
            </fieldset>
            <p id="guest-status" className="portal-login__message" role="status" aria-live="polite">{guestMessage}</p>
            {security.isError && !factorId && <div role="alert"><p>Guest sign-in settings could not be loaded.</p><button type="button" className="btn btn-secondary sm" onClick={() => void security.refetch()}>Retry guest settings</button></div>}
          </form>
          <div className="portal-login__invitation"><strong>First time here?</strong><p>Open your invitation email to set up your account.<br />Need access? Contact your ITA coordinator.</p></div>
        </section>
      </div>
    </main>
  );
}
